import React from 'react';
import { IListingData } from '@models/listing';
import { IFilterModel, ITableSettings } from '.';
import { IAbstractRecord } from '@models';
import { makeStyles } from 'tss-react/mui';
import { MAIN_THEME } from '@styles';
import { IEnhancedMenuItem } from '../../table/types';
import ListingTableWithPagination from '../../table/with-pagination/ListingTableWithPagination';
import ListingTable from '../../table/ListingTable';
import useActionFilter from '../../../hooks/useActionFilter';
import ActionMenu from '../../menu/ActionMenu';
import SimpleActionBar from '../../menu/SimpleActionBar';

export interface IListingWidgetProps {
  name: string;
  title: string;
  inlineTitle?: string;
  inline?: boolean;
  actions: IEnhancedMenuItem[];
  data: IListingData;
  tableSettings: ITableSettings;
  pageContext?: string;
  onPageChange?: (filterModel: IFilterModel) => Promise<void>;
  dropdownActionsNames?: string[];
  defaultOrderByColumn?: string;
  orderByAscendingByDefault?: boolean;
  secondOrderByColumn?: string;
  secondOrderByAscendingByDefault?: boolean;
  disableSelection?: boolean;
  disabledSorting?: boolean;
  context?: IAbstractRecord;
  usePagination?: boolean;
  loading?: boolean;
  pageSizeOptions?: number[];
  forceShowSelectColumn?: boolean;
  forceHideToolbar?: boolean;
  handleCellValueChanged?(
    rowKey: number,
    property: string,
    value: unknown
  ): void;
  tableClasses?: {
    inputContainer?: string;
  };
}

const useStyles = makeStyles()(() => ({
  header: {
    margin: '0 0 1em',
    display: 'grid',
    gridTemplateAreas: `'title actions'`,
    gridGap: '1em',
  },
  title: {
    display: 'block',
    textAlign: 'left',
    fontSize: MAIN_THEME.fontSize.lg,
    fontFamily: MAIN_THEME.font.bold,
    letterSpacing: 0,
    color: MAIN_THEME.color.black,
    fontWeight: 'normal',
    margin: 0,
    gridArea: 'title',
    alignSelf: 'center',
  },
  actions: {
    display: 'flex',
    gridArea: 'actions',
    justifyContent: 'flex-end',
  },
  toggleGroup: {
    margin: '0 0.5em 0 0',
  },
  toggleButton: {
    textTransform: 'capitalize',
    '&.MuiToggleButton-root.Mui-selected': {
      color: MAIN_THEME.color.white,
      backgroundColor: MAIN_THEME.color.black,
    },
  },
  toggle: {
    backgroundColor: MAIN_THEME.color.white,
    color: MAIN_THEME.color.black,
    height: '32px',
  },
  secondaryActions: {
    boxSizing: 'border-box',
    display: 'inline-flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    color: MAIN_THEME.color.black,
    margin: '0 0 0 0.5em',
    backgroundColor: 'white',
  },
  dropdown: {
    width: 'unset',
  },
  dropdownField: {
    lineHeight: 1.5,
    textAlign: 'left',
    color: MAIN_THEME.color.black,
    display: 'flex',
  },
}));

const ListingWidget: React.FC<IListingWidgetProps> = ({
  name,
  actions = [],
  data,
  inline,
  tableSettings,
  title,
  dropdownActionsNames,
  inlineTitle,
  defaultOrderByColumn,
  orderByAscendingByDefault = false,
  secondOrderByColumn,
  secondOrderByAscendingByDefault = false,
  disableSelection,
  disabledSorting,
  context,
  usePagination,
  pageContext,
  loading,
  pageSizeOptions,
  onPageChange,
  forceShowSelectColumn,
  handleCellValueChanged,
  forceHideToolbar,
  tableClasses,
}) => {
  const { classes } = useStyles();
  const [widgetActions, entityActions] = useActionFilter(actions);

  const handlePageChange = (
    page: number,
    searchKey = '',
    rowsPerPage = 0,
    order = '',
    orderBy = ''
  ) => {
    try {
      const filterModel: IFilterModel = {
        searchKey,
        orderBy: orderBy,
        descending: order === 'desc',
        pagination: {
          pageSize: rowsPerPage !== 0 ? rowsPerPage : data.pageSize,
          pageNumber: page,
        },
      };

      return new Promise<void>(() => {
        if (onPageChange) {
          void onPageChange(filterModel);
        }
      });
    } catch (error) {
      return new Promise<void>(() => {});
    }
  };

  const renderActionMenu = () => {
    if (
      widgetActions &&
      widgetActions.length > 0 &&
      dropdownActionsNames &&
      dropdownActionsNames.length > 0
    ) {
      const dropdownActions = widgetActions.filter((action) =>
        dropdownActionsNames.includes(action.name)
      );
      if (dropdownActions.length > 0) {
        return (
          <div className={classes.secondaryActions}>
            <ActionMenu actions={dropdownActions} title={'Actions'} />
          </div>
        );
      }
    }
    return <></>;
  };

  const renderTableWithPagination = () => (
    <ListingTableWithPagination
      name={name}
      pageContext={pageContext}
      headers={tableSettings.headers}
      rowSettings={tableSettings.rowSettings}
      data={data}
      dropdownActionsNames={dropdownActionsNames}
      actions={inline ? actions : entityActions}
      inline={inline}
      filterableNames={tableSettings.filterableNames}
      inlineTitle={inlineTitle}
      defaultOrderByColumn={defaultOrderByColumn}
      orderByAscendingByDefault={orderByAscendingByDefault}
      secondOrderByColumn={secondOrderByColumn}
      secondOrderByAscendingByDefault={secondOrderByAscendingByDefault}
      disableSelection={disableSelection}
      disabledSorting={disabledSorting}
      context={context}
      loader={loading}
      pageSizeOptions={pageSizeOptions}
      usePagination={usePagination}
      onPageChange={handlePageChange}
      handleCellValueChanged={handleCellValueChanged}
      // forceShowSelectColumn={forceShowSelectColumn}
    />
  );

  const renderTable = () => (
    <ListingTable
      name={name}
      headers={tableSettings.headers}
      rowSettings={tableSettings.rowSettings}
      data={data}
      dropdownActionsNames={dropdownActionsNames}
      actions={actions}
      forceHideToolbar={forceHideToolbar}
      inline={inline}
      filterableNames={tableSettings.filterableNames}
      splitCharacter={tableSettings.splitCharacter}
      inlineTitle={inlineTitle}
      defaultOrderByColumn={defaultOrderByColumn}
      orderByAscendingByDefault={orderByAscendingByDefault}
      secondOrderByColumn={secondOrderByColumn}
      secondOrderByAscendingByDefault={secondOrderByAscendingByDefault}
      disableSelection={disableSelection}
      context={context}
      loader={loading}
      pageSizeOptions={pageSizeOptions}
      forceShowSelectColumn={forceShowSelectColumn}
      handleCellValueChanged={handleCellValueChanged}
      tableClasses={tableClasses}
    />
  );

  return (
    <>
      {(title || (!inline && actions.length > 0)) && (
        <section className={classes.header}>
          <h1 className={classes.title}>{title}</h1>
          <div className={classes.actions}>
            {!inline && widgetActions && (
              <>
                <SimpleActionBar
                  items={widgetActions.filter(
                    (action) =>
                      !dropdownActionsNames ||
                      !dropdownActionsNames.includes(action.name)
                  )}
                />
                {renderActionMenu()}
              </>
            )}
          </div>
        </section>
      )}
      {tableSettings && usePagination
        ? renderTableWithPagination()
        : renderTable()}
    </>
  );
};

export default ListingWidget;
